import React from "react";
import { useTranslation } from "react-i18next";
import {
  Account,
  CreateTppWebFormV2ParamsMultibankingApiProviderEnum,
} from "@rentablo/api/apiv1";
import { useMyself } from "@api";
import { getUpdateBankConnectionUrl } from "@helpers";
import { createAndRedirectToTppWebForm } from "@rentablo/bank-import";
import { ResponsiveDropdownItem } from "@components";
import UpdateIcon from "@icons/myicons/refresh.svg?react";
import AddIcon from "@icons/myicons/plus.svg?react";
import EditIcon from "@icons/myicons/edit.svg?react";
import UploadIcon from "@icons/myicons/cloud-storage-upload.svg?react";
import ManageBankConnectionIcon from "@icons/myicons/bank-edit.svg?react";
import HideIcon from "@icons/myicons/eye-cross.svg?react";
import UnhideIcon from "@icons/myicons/eye.svg?react";
import CancelIcon from "@icons/myicons/delete-circle.svg?react";

interface Options {
  account?: Account;
  handleHideAccount: () => void;
  handleOpenDeleteModal: () => void;
}

export const useSingleAccountMenuItems = ({
  account,
  handleHideAccount,
  handleOpenDeleteModal,
}: Options): ResponsiveDropdownItem[] => {
  const { t } = useTranslation();
  const { data: myself } = useMyself();
  const isQA = myself?.roles?.includes("ROLE_QUALITY_ASSURANCE");

  const handleReconnect = async () => {
    if (!account || !account.bankConnectionStatus?.restoreId) return;

    await createAndRedirectToTppWebForm({
      multibankingApiProvider:
        account.bankConnectionType as CreateTppWebFormV2ParamsMultibankingApiProviderEnum,
      restoreId: account.bankConnectionStatus?.restoreId,
    });
  };

  const updateConnectionUrl = getUpdateBankConnectionUrl(account);
  const hasBankConnection = Boolean(account?.bankConnectionType);
  const items: ResponsiveDropdownItem[] = [];

  if (updateConnectionUrl) {
    items.push({
      label: t("singleAccountPage.header.update"),
      testId: "account-update",
      to: updateConnectionUrl,
      icon: <UpdateIcon />,
    });
  }

  if (isQA && account?.bankConnectionStatus?.status === "INACTIVE") {
    items.push({
      label: t("depotPage.headerMenu.reconnect"),
      testId: "depot-reconnect",
      onClick: handleReconnect,
      icon: <UpdateIcon />,
    });
  }

  items.push({
    label: t("depotPage.headerMenu.uploadCsv"),
    testId: "csv-upload",
    to: `/depots/${account?.id}/csv-drop`,
    icon: <UploadIcon />,
  });

  items.push({
    label: t("singleAccountPage.header.addTransaction"),
    testId: "add-cash-transaction",
    to: `/accounts/${account?.id}/transactions/add-manually`,
    icon: <AddIcon width="24px" height="14px" />,
  });

  items.push({
    label: t("singleAccountPage.header.edit"),
    testId: "edit",
    to: `/accounts/${account?.id}/edit`,
    icon: <EditIcon />,
  });

  if (hasBankConnection) {
    items.push({
      label: t("singleAccountPage.header.manageBankConnection"),
      testId: "manage-bank-connection",
      to: `/settings/bankConnections/${account?.bankConnectionType}/${account?.bankConnectionId}`,
      icon: <ManageBankConnectionIcon />,
    });
  }

  items.push({
    label: account?.isHidden
      ? t("singleAccountPage.header.show")
      : t("singleAccountPage.header.hide"),
    testId: account?.isHidden ? "unhide-account" : "hide-account",
    onClick: handleHideAccount,
    icon: account?.isHidden ? <UnhideIcon /> : <HideIcon />,
    startsGroup: true,
  });

  items.push({
    label: t("singleAccountPage.header.delete"),
    testId: "delete-account-item",
    onClick: handleOpenDeleteModal,
    icon: <CancelIcon />,
    color: "error.main",
    startsGroup: true,
  });

  return items;
};
